import styled from 'styled-components';
import { gql } from '@apollo/client';
import { shuffle } from 'lodash';
import { NextPage } from 'next';
import Link from 'next/link';
import { FOOTER_QUERY } from '../components/footer';
import { HEADER_QUERY, Header } from '../components/header';
import { HomeHero } from '../components/home-hero';
import {
  LATEST_UPDATES_QUERY,
  LatestUpdates,
} from '../components/latest-updates';
import { ContentStackClient } from '../lib/contentstack';
import { AllLatestUpdates, Home2025, SysAsset } from '../types/contentstack';
import { IPageProps } from '../types/page';
import { LogoCarousel } from '../components/logo-carousel';
import { Footer } from '../components/footer';
import { DataflowCarousel } from '../components/dataflow';
import { Quotes } from '../components/quotes';
import { mediaScreen } from '../utils/mediaScreen';
import { ResponsiveContainer } from '../components/responsive';
import { Button } from '../components/ui/button';
import { getAssetUrl } from '../utils/contentstack';
import Lottie from 'react-lottie';
import { RichTabs } from '../components/rich-tabs';
import {
  SectionEyebrow,
  SectionHeadline,
  SectionSubheadline,
} from '../components/section';

export const HomePage: NextPage<
  IPageProps<Home2025> & {
    integrationsAnimation: object;
    customer_logos: { file: SysAsset }[];
  }
> = ({
  header,
  page,
  footer,
  latest_updates,
  customer_logos,
  integrationsAnimation,
}) => {
  return (
    <>
      <Header
        pageTitle={page?.title}
        pageDescription={page?.description}
        {...header}
      />

      <HomeHero {...page?.hero} />

      <DataflowSection id="dataflow">
        <WidthDelimiter>
          <SectionHeadline style={{ maxWidth: '100%' }}>
            {page?.dataflow_carousel?.headline}
          </SectionHeadline>
          <SectionSubheadline>
            {page?.dataflow_carousel?.subheadline}
          </SectionSubheadline>
          <DataflowCarousel items={page?.dataflow_carousel?.items} />

          {!!page?.dataflow_carousel?.primary_button?.title && (
            <div className="buttons" style={{ marginTop: 60 }}>
              <Link
                href={page?.dataflow_carousel?.primary_button?.href!}
                legacyBehavior
                passHref
              >
                <Button mType="primary" as="a">
                  {page?.dataflow_carousel?.primary_button?.title}
                </Button>
              </Link>
            </div>
          )}
        </WidthDelimiter>
      </DataflowSection>

      <LogoCarousel direction="right" data={customer_logos} />

      <HowItWorksSection id="how-it-works">
        <div className="inner">
          <div className="texts">
            <SectionEyebrow>{page?.how_it_works?.eyebrow}</SectionEyebrow>
            <SectionHeadline
              dangerouslySetInnerHTML={{
                __html: page?.how_it_works?.headline || '',
              }}
            />

            <SectionSubheadline
              dangerouslySetInnerHTML={{
                __html: page?.how_it_works?.subheadline || '',
              }}
            />
          </div>

          <div className="steps" aria-label="How it works steps">
            {page?.how_it_works?.steps?.map((step, idx) => {
              const icon = getAssetUrl(step?.iconConnection);

              return (
                <div className="step" key={idx}>
                  <div className="step-head">
                    <img
                      src={icon}
                      alt=""
                      aria-hidden="true"
                      className="icon"
                      width={60}
                      height={60}
                    />

                    <h3
                      className="step-title"
                      dangerouslySetInnerHTML={{ __html: step?.title! }}
                    />
                  </div>

                  <p
                    className="step-description"
                    dangerouslySetInnerHTML={{
                      __html: step?.description || '',
                    }}
                  />
                </div>
              );
            })}
          </div>

          <div className="buttons">
            <Link
              href={page?.how_it_works?.primary_button?.href || ''}
              passHref
              legacyBehavior
            >
              <Button as="a" mType="primary">
                {page?.how_it_works?.primary_button?.title}
              </Button>
            </Link>

            <Link
              href={page?.how_it_works?.secondary_button?.href || ''}
              passHref
              legacyBehavior
            >
              <Button as="a" mType="secondary">
                {page?.how_it_works?.secondary_button?.title}
              </Button>
            </Link>
          </div>
        </div>
      </HowItWorksSection>

      <IntegrationsSection id="integrations">
        <div className="integrations-animation">
          <Lottie
            isClickToPauseDisabled={true}
            options={{
              animationData: integrationsAnimation,
              autoplay: true,
              loop: true,
            }}
          />

          <Link
            href={page?.integrations?.primary_button?.href || ''}
            passHref
            legacyBehavior
          >
            <Button as="a" mType="primary" className="mobile-only">
              {page?.integrations?.primary_button?.title}
            </Button>
          </Link>
        </div>

        <div className="integrations-content">
          <SectionEyebrow>{page?.integrations?.eyebrow}</SectionEyebrow>
          <SectionHeadline>{page?.integrations?.headline}</SectionHeadline>

          <SectionSubheadline>
            {page?.integrations?.subheadline}
          </SectionSubheadline>

          <Link
            href={page?.integrations?.primary_button?.href || ''}
            passHref
            legacyBehavior
          >
            <Button as="a" mType="primary" className="desktop-only">
              {page?.integrations?.primary_button?.title}
            </Button>
          </Link>
        </div>
      </IntegrationsSection>

      <StatsSection aria-label="Platform stats" id="stats">
        <div className="stats-grid">
          {page?.stats?.map((stat, idx) => {
            const icon = getAssetUrl(stat?.iconConnection);

            return (
              <div key={idx} className={`stat ${idx === 0 ? 'highlight' : ''}`}>
                <div className="stat-icon">
                  <img src={icon} className="icon" />
                </div>

                <div className="stat-content">
                  <h3
                    className="stat-number"
                    dangerouslySetInnerHTML={{ __html: stat?.number || '' }}
                  />

                  <p className="stat-label">{stat?.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </StatsSection>

      <ChecksSection id="checks">
        <ResponsiveContainer>
          <div className="checks-inner">
            <div className="checks-texts">
              <SectionEyebrow invert={true}>
                {page?.checks?.eyebrow}
              </SectionEyebrow>

              <SectionHeadline invert={true}>
                {page?.checks?.headline}
              </SectionHeadline>

              <ul className="checks-list" aria-label="Key platform benefits">
                {page?.checks?.items?.map((item, idx) => (
                  <li className="checks-item" key={idx}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="checks-icon" aria-hidden="true">
                        <img src="/icons/checkmark.svg" alt="" />
                      </div>

                      <h3
                        className="checks-title"
                        dangerouslySetInnerHTML={{
                          __html: item?.title || '',
                        }}
                      />
                    </div>

                    <p
                      className="checks-description"
                      dangerouslySetInnerHTML={{
                        __html: item?.description || '',
                      }}
                    />
                  </li>
                ))}
              </ul>

              <div className="buttons">
                <Link
                  href={page?.checks?.primary_button?.href || ''}
                  passHref
                  legacyBehavior
                >
                  <Button as="a" mType="primary">
                    {page?.checks?.primary_button?.title}
                  </Button>
                </Link>
                <Link
                  href={page?.checks?.secondary_button?.href || ''}
                  passHref
                  legacyBehavior
                >
                  <Button as="a" mType="secondary" invert={true}>
                    {page?.checks?.secondary_button?.title}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </ResponsiveContainer>

        <img
          src="/img/checks-image.svg"
          alt="Illustration for key platform benefits"
          className="checks-image"
        />
      </ChecksSection>

      <IndustriesSection id="industries">
        <div className="inds-head">
          <SectionEyebrow>{page?.industries?.eyebrow}</SectionEyebrow>
          <SectionHeadline>{page?.industries?.headline}</SectionHeadline>
          <SectionSubheadline>
            {page?.industries?.subheadline}
          </SectionSubheadline>
        </div>

        <RichTabs
          className="inds-tabs"
          moreLabel="More Industries"
          items={
            page?.industries?.items?.map((item) => ({
              ...item,
              icon: getAssetUrl(item?.iconConnection),
              image: getAssetUrl(item?.imageConnection),
            })) ?? []
          }
        />
      </IndustriesSection>

      <Quotes {...(page?.quotes || {})} />

      <LatestUpdates {...latest_updates} />

      <Footer sections={footer?.sections} {...page?.footer} />
    </>
  );
};

export async function getServerSideProps() {
  try {
    const [{ data: data }] = await Promise.all([
      ContentStackClient().query<{
        page: { data: Home2025[] };
        latest_updates?: AllLatestUpdates;
      }>({
        query: PAGE_QUERY,
      }),
    ]);

    const page = data?.page?.data?.[0];

    const latest_updates = data?.latest_updates?.items?.[0] ?? null;

    const customer_logos =
      page?.customer_logosConnection?.edges?.map((logo) => ({
        file: logo?.node,
      })) ?? [];

    const integrationsAnimation = await fetch(
      getAssetUrl(page?.integrations?.animation_lottie_fileConnection)
    ).then((res) => res.json());

    return {
      props: {
        ...data,

        latest_updates,

        customer_logos: shuffle(customer_logos).slice(0, 50),

        integrationsAnimation,

        page: {
          ...page,
          customers_logosConnection: null,
        },
      },
    };
  } catch (e: any) {
    console.log(JSON.stringify(e, null, 2));
    return { props: {} };
  }
}

const PAGE_QUERY = gql`
  query homeQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}
    ${LATEST_UPDATES_QUERY}

    page: all_home_2025 {
      data: items {
        title
        description
        hero {
          eyebrow
          headline
          subheadline
          primary_button {
            title
            href
          }
          secondary_button {
            title
            href
          }
          background_videoConnection {
            edges {
              node {
                url
              }
            }
          }
          background_video_posterConnection {
            edges {
              node {
                url
              }
            }
          }
        }
        dataflow_carousel {
          headline
          subheadline
          primary_button {
            title
            href
          }
          items {
            image_desktopConnection {
              edges {
                node {
                  url
                }
              }
            }
            image_mobileConnection {
              edges {
                node {
                  url
                }
              }
            }
          }
        }
        customer_logosConnection {
          edges {
            node {
              url
              dimension {
                width
                height
              }
            }
          }
        }
        featured_quote {
          author
          text
        }
        how_it_works {
          eyebrow
          headline
          subheadline
          steps {
            title
            description
            iconConnection {
              edges {
                node {
                  url
                }
              }
            }
          }
          primary_button {
            title
            href
          }
          secondary_button {
            title
            href
          }
        }
        integrations {
          eyebrow
          headline
          subheadline
          primary_button {
            title
            href
          }
          animation_lottie_fileConnection {
            edges {
              node {
                url
              }
            }
          }
        }
        stats {
          number
          label
          iconConnection {
            edges {
              node {
                url
              }
            }
          }
        }
        checks {
          eyebrow
          headline
          items {
            title
            description
          }
          primary_button {
            title
            href
          }
          secondary_button {
            title
            href
          }
        }
        industries {
          eyebrow
          headline
          subheadline
          items {
            title
            description
            iconConnection {
              edges {
                node {
                  url
                }
              }
            }
            imageConnection {
              edges {
                node {
                  url
                }
              }
            }
            primary_button {
              title
              href
            }
            secondary_button {
              title
              href
            }
          }
        }
        quotes {
          eyebrow
          quotes {
            author
            text
            link {
              title
              href
            }
          }
        }
        footer {
          headline
          subheadline
          primary_button {
            title
            href
          }
          secondary_button {
            title
            href
          }
          third_button {
            title
            href
          }
        }
      }
    }
  }
`;

const WidthDelimiter = styled.div`
  max-width: 1600px;
  position: relative;
  margin: 0 auto;
`;

const DataflowSection = styled.section`
  padding: 100px 0;
  text-align: center;

  ${SectionHeadline} {
    padding: 0 32px;
  }

  ${SectionSubheadline} {
    margin: 0 auto 40px;
    max-width: 720px;
    padding: 0 32px;
  }

  ${mediaScreen('xs', 'md')} {
    padding: 60px 0;
  }
`;

const HowItWorksSection = styled(ResponsiveContainer)`
  padding-bottom: 120px;
  padding-top: 60px;
  position: relative;

  .inner {
    margin: 0 auto;
    box-sizing: border-box;
  }

  .texts {
    max-width: 760px;
    margin-bottom: 56px;
  }

  ${SectionEyebrow} {
    display: inline-block;
    margin-bottom: 28px;
  }

  ${SectionHeadline} {
    margin: 0 0 28px;
  }

  ${SectionSubheadline} {
    margin: 0;
  }

  .steps {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
    margin-bottom: 56px;
  }

  .step {
    background: ${(p) => p.theme.colors.slate2};
    border-radius: 8px;
    padding: 24px;
    position: relative;
    display: flex;
    flex-direction: column;
    text-align: left;
    overflow: hidden;
  }

  .step-head {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
  }

  .icon {
    width: 50px;
    height: 50px;
    margin-right: 16px;
  }

  .step-title {
    font-size: 24px;
    line-height: 1.35;
    color: ${(p) => p.theme.colors.white};
    font-weight: 700;
    margin: 0;

    b {
      font-weight: 700;
      color: ${(p) => p.theme.colors.teal};
    }
  }

  .step-description {
    font-size: 16px;
    line-height: 1.5;
    color: ${(p) => p.theme.colors.white};
    font-weight: 400;
    margin: 0;
  }

  .buttons {
    display: flex;
    flex-direction: row;
    gap: 24px;
  }

  ${mediaScreen('xs', 'md')} {
    padding-bottom: 80px;

    ${SectionHeadline} {
      margin-bottom: 20px;
    }

    .steps {
      grid-template-columns: 1fr;
      gap: 20px;
      margin-bottom: 40px;
    }

    .step-title {
      font-size: 28px;
    }

    .step-description {
      font-size: 16px;
      line-height: 1.5;
    }

    .buttons {
      flex-direction: column;
      gap: 16px;
    }

    .buttons {
      width: 100%;
      align-items: stretch;
      text-align: center;
    }
  }
`;

const IntegrationsSection = styled(ResponsiveContainer)`
  display: flex;
  align-items: center;
  padding-bottom: 120px;

  .integrations-animation {
    flex: 0.5;
    margin-right: 100px;
  }

  .integrations-content {
    flex: 0.5;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;

    ${SectionEyebrow} {
      margin-top: 0;
    }

    .mButton {
      margin-top: 32px;
    }
  }

  .mobile-only {
    display: none;
  }

  ${mediaScreen('xs', 'md')} {
    flex-direction: column-reverse;
    padding-bottom: 80px;

    .mobile-only {
      display: flex;
    }

    .desktop-only {
      display: none;
    }

    .mButton {
      width: 100%;
      margin-top: 32px;
    }

    .integrations-content {
      width: 100%;
    }

    .integrations-animation {
      width: 100%;
      margin: 32px auto 0;
    }
  }
`;

const StatsSection = styled(ResponsiveContainer)`
  padding-bottom: 120px;
  position: relative;

  .stats-grid {
    display: grid;
    grid-template-columns: minmax(440px, 1.1fr) repeat(2, 1fr);
    gap: 24px;
    align-items: stretch;
  }

  .stat {
    background: #262f40;
    border-radius: 8px;
    padding: 28px;
    position: relative;
    overflow: hidden;
    display: flex;
    text-align: left;
  }

  .stat-icon {
    margin-right: 20px;

    img {
      width: 50px;
    }
  }

  .stat-number {
    font-size: 50px;
    line-height: 1;
    font-weight: 400;
    margin: 0;
    margin-bottom: 10px;
    color: ${(p) => p.theme.colors.white};

    b {
      color: ${(p) => p.theme.colors.teal};
      font-weight: 400;
    }
  }

  .stat-label {
    font-size: 16px;
    line-height: 1.5;
    margin: 0;
    font-weight: 400;
    color: ${(p) => p.theme.colors.white};
  }

  .stat.highlight {
    grid-row: span 2;
    flex-direction: column;
    background:
      radial-gradient(
        circle at top left,
        rgba(255, 150, 50, 0.25),
        transparent 60%
      ),
      radial-gradient(
        circle at top right,
        rgba(50, 200, 150, 0.25),
        transparent 70%
      ),
      radial-gradient(circle at bottom left, #262f40, transparent 80%), #262f40;

    .stat-icon {
      margin-bottom: 40px;

      img {
        width: 130px;
      }
    }

    .stat-number {
      font-size: 70px;
      margin-bottom: 24px;
    }

    .stat-label {
      font-size: 20px;
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-bottom: 80px;

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }
    .stat {
      flex-direction: column;
    }
    .stat-icon {
      display: none;
    }
    .stat-number {
      font-size: 32px;
    }
    .stat-label {
      font-size: 16px;
    }
    .stat.highlight {
      grid-column: 1 / -1;
      grid-row: auto;

      .stat-icon {
        display: block;

        img {
          width: 100px;
        }
      }

      .stat-number {
        font-size: 60px;
      }

      .stat-label {
        font-size: 20px;
      }
    }
  }
`;

const ChecksSection = styled.section`
  position: relative;
  padding-bottom: 120px;
  margin-bottom: 100px;
  padding-top: 80px;
  overflow: hidden;
  background: white;

  ${SectionHeadline} {
    max-width: 500px;
    margin-bottom: 50px;
  }

  .checks-inner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    position: relative;
  }
  .checks-texts {
    flex: 0.55;
    max-width: 550px;
    position: relative;
    z-index: 2;
  }
  .checks-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .checks-item + .checks-item {
    border-top: 1px solid rgba(74, 59, 207, 0.3);
    margin-top: 32px;
    padding-top: 32px;
  }
  .checks-icon {
    width: 24px;
    height: 24px;
    background: ${(p) => p.theme.colors.teal};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    flex-shrink: 0;
    position: relative;
    top: -1px;

    img {
      width: 16px;
      height: 16px;
      filter: brightness(0) invert(1);
    }
  }
  .checks-title {
    font-size: 22px;
    line-height: 28px;
    font-weight: 700;
    margin: 0;
    color: ${(p) => p.theme.colors.slate1};
  }
  .checks-description {
    font-size: 16px;
    line-height: 1.5;
    margin: 10px 0 0;
    color: ${(p) => p.theme.colors.slate1};
  }
  .buttons {
    display: flex;
    margin-top: 64px;
    gap: 24px;
  }
  .checks-image {
    position: absolute;
    top: 10%;
    right: 0;
    height: auto;
    pointer-events: none;
  }

  ${mediaScreen('md', 'lg')} {
    .checks-image {
      width: 70%;
      top: 20%;
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-bottom: 60px;
    margin-bottom: 60px;

    display: flex;
    flex-direction: column-reverse;

    .buttons {
      display: none;
    }

    .checks-inner {
      flex-direction: column;
      padding-top: 65%;
    }
    .checks-texts {
      flex: none;
      max-width: 100%;
    }
    .checks-image {
      position: absolute;
      right: 0;
      top: -9%;
      max-width: 140%;
    }
    .checks-item + .checks-item {
      margin-top: 24px;
      padding-top: 24px;
    }
  }
`;

const IndustriesSection = styled(ResponsiveContainer)`
  position: relative;
  padding-bottom: 120px;
  display: flex;
  flex-direction: column;

  .inds-head {
    margin-bottom: 56px;
    margin-left: auto;
    margin-right: auto;

    ${SectionEyebrow} {
      margin-top: 0;
    }

    > * {
      text-align: center;
      width: 100%;
      max-width: 1200px;
    }
  }

  ${SectionHeadline} {
    margin-bottom: 24px;
  }

  ${SectionSubheadline} {
    margin: 0;
  }

  ${mediaScreen('xs', 'md')} {
    padding-bottom: 80px;
    .inds-head {
      margin-bottom: 32px;
    }
    .inds-tabs {
      margin-top: 8px;
    }
  }
`;

export default HomePage;
